import { useState, useMemo, useEffect } from 'react';
import { useLocalStorageWithTtl } from 'infrastructure/hooks/utils';

import { formSections } from './form-sections';
import { generateCkdCcmClinicalNotesTemplate } from './template';

type IValue = Record<string, unknown | unknown[]>;

export const getCkdCcmClinicalNoteStoreKey = (patientGuid: string) =>
  `ckd-ccm-clinical-note-${patientGuid}`;

interface IProps {
  patientGuid: string;
  onSubmit: (text: string) => void;
  toggleOpen: () => void;
}

export const useNote = ({ onSubmit, toggleOpen, patientGuid }: IProps) => {
  const tempStorageKey = getCkdCcmClinicalNoteStoreKey(patientGuid);
  const { getValue, setValue: setLocalStorageValue } =
    useLocalStorageWithTtl<IValue>(tempStorageKey);
  const initValue = getValue() || {};

  const [values, setValues] = useState<IValue>(() => initValue);
  const [currentStep, setCurrentStep] = useState(0);
  const goToPrevStep = () => setCurrentStep((prev) => prev - 1);
  const goToNextStep = () => setCurrentStep((prev) => prev + 1);
  const setValue = (value: IValue) => {
    setValues((prev) => ({ ...prev, ...value }));
  };

  const errorSections = useMemo(() => {
    const sections: number[] = [];
    const fields = formSections.map((section) => {
      return section.forms.filter((form) => {
        let { isRequired } = form;
        if (form.requiredIf) {
          isRequired = values[form.requiredIf.key] === form.requiredIf.value;
        }
        if (form.notRequiredIf) {
          isRequired =
            values[form.notRequiredIf.key] !== form.notRequiredIf.value;
          form.isRequired = isRequired;
        }
        if (isRequired) {
          const value = values[form.id];
          if ((Array.isArray(value) && !value.length) || !value) {
            return form;
          }
        }
        return null;
      });
    });
    fields.forEach((el, index) => {
      if (el.length) {
        sections.push(index + 1);
      }
    });
    return sections;
  }, [values]);

  const reduceDataBySteps = () => {
    return {
      STEP1: {
        howLongTreatedCkd: values.howLongTreatedCkd,
        howLongTreatedCkdNote: values.howLongTreatedCkdNote,
        effectsOnBody: values.effectsOnBody,
        effectsOnBodyNote: values.effectsOnBodyNote,
        hospitalized: values.hospitalized,
        outpatientServices: values.outpatientServices,
        familyHistory: values.familyHistory,
        pretransplantAssessment: values.pretransplantAssessment,
        pretransplantAssessmentNote: values.pretransplantAssessmentNote,
      },
      STEP2: {
        recognizesSignsSymptoms: values.recognizesSignsSymptoms,
        snInstructions: values.snInstructions,
        notifyProvider: values.notifyProvider,
        symptomsTeachBack: values.symptomsTeachBack,
        symptomsVerbalizeEducation: values.symptomsVerbalizeEducation,
        symptomsVerbalizeEducationNote: values.symptomsVerbalizeEducationNote,
      },
      STEP3: {
        limitingFluids: values.limitingFluids,
        helpQuenchThirst: values.helpQuenchThirst,
        fluidsTeachBack: values.fluidsTeachBack,
        fluidsVerbalizeEducation: values.fluidsVerbalizeEducation,
        fluidsVerbalizeEducationNote: values.fluidsVerbalizeEducationNote,
        monitorWeight: values.monitorWeight,
        monitorWeightImportant: values.monitorWeightImportant,
        haveScale: values.haveScale,
        measureWeight: values.measureWeight,
        recentWeights: values.recentWeights,
        dailyWeightMonitoring: values.dailyWeightMonitoring,
        limitPhosphorusInDiet: values.limitPhosphorusInDiet,
        weightTeachBack: values.weightTeachBack,
        weightVerbalizeEducation: values.weightVerbalizeEducation,
        weightVerbalizeEducationNote: values.weightVerbalizeEducationNote,
        reinforcementNote: values.reinforcementNote,
      },
    };
  };

  const submit = () => {
    const dataValues = reduceDataBySteps();

    const resultText = generateCkdCcmClinicalNotesTemplate(dataValues);
    const formattedResultText = resultText
      .replaceAll('&#x2F;', '/')
      .replaceAll(/  +/g, '')
      .replaceAll(/\n{2,}/g, '\n \n')
      .replaceAll('&#39;', "'");

    onSubmit(formattedResultText);
    toggleOpen();
    setLocalStorageValue(values);
    setCurrentStep(0);
    setValues({});
  };

  useEffect(() => {
    setValues(initValue || {});
  }, []);

  return {
    submit,
    goToPrevStep,
    goToNextStep,
    setValue,
    currentStep,
    errorSections,
    values,
  };
};
